/**
 * TechnologistManagement Component
 * Admin interface for managing facility technologists
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatServerDate } from '../utils/dateFormat';
import './TechnologistManagement.css';

function TechnologistManagement() {
  const [technologists, setTechnologists] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterFacility, setFilterFacility] = useState('');
  const [filterProfessionRole, setFilterProfessionRole] = useState('');
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    facilityId: '',
    professionRole: 'sonographer'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadTechnologists();
    loadFacilities();
    loadStats();
  }, [filterFacility, filterProfessionRole]);

  const loadTechnologists = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFacility) params.append('facilityId', filterFacility);
      if (filterProfessionRole) params.append('professionRole', filterProfessionRole);
      params.append('isActive', 'true'); // Only show active by default

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTechnologists(response.data.technologists || []);
    } catch (error) {
      console.error('Error loading technologists:', error);
      alert('Failed to load technologists');
    } finally {
      setLoading(false);
    }
  };

  const loadFacilities = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/facilities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFacilities(response.data.facilities || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadStats = async () => {
    try {
      const params = filterFacility ? `?facilityId=${filterFacility}` : '';
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists/stats${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Technologist created successfully!');
      setShowForm(false);
      setFormData({ fullName: '', facilityId: '', professionRole: 'sonographer' });
      loadTechnologists();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create technologist');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists/${editing.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Technologist updated successfully!');
      setEditing(null);
      setShowForm(false);
      setFormData({ fullName: '', facilityId: '', professionRole: 'sonographer' });
      loadTechnologists();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update technologist');
    }
  };

  const handleEdit = (tech) => {
    setEditing(tech);
    setFormData({
      fullName: tech.full_name || '',
      facilityId: tech.facility_id || '',
      professionRole: tech.profession_role || 'sonographer'
    });
    setShowForm(true);
  };

  const handleDelete = async (tech) => {
    if (!window.confirm(`Delete technologist "${tech.full_name}"?`)) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists/${tech.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Technologist deleted successfully!');
      loadTechnologists();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete technologist');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setFormData({ fullName: '', facilityId: '' });
  };

  return (
    <div className="technologist-management">
      <div className="tech-header">
        <h2>Technologist Management</h2>
        <p className="tech-description">
          Manage the list of technologists available for selection in forms. 
          Technologists are organized by facility.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>{stats.total || 0}</h3>
            <p>Total Technologists</p>
          </div>
          <div className="stat-card">
            <h3>{stats.active || 0}</h3>
            <p>Active</p>
          </div>
          <div className="stat-card">
            <h3>{stats.inactive || 0}</h3>
            <p>Inactive</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="tech-controls">
        <div className="tech-actions">
          <button 
            onClick={() => { setShowForm(true); setEditing(null); }} 
            className="btn-primary"
          >
            + Add Technologist
          </button>
        </div>

        <div className="tech-filter">
          <label>Filter by Facility:</label>
          <select
            value={filterFacility}
            onChange={(e) => setFilterFacility(e.target.value)}
          >
            <option value="">All Facilities</option>
            {facilities.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>

        <div className="tech-filter">
          <label>Filter by Role:</label>
          <select
            value={filterProfessionRole}
            onChange={(e) => setFilterProfessionRole(e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="sonographer">Sonographer</option>
            <option value="nurse">Nurse</option>
          </select>
        </div>
      </div>

      {/* Form Panel */}
      {showForm && (
        <div className="form-panel">
          <h3>{editing ? 'Edit Technologist' : 'Add New Technologist'}</h3>
          <form onSubmit={editing ? handleUpdate : handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                  required
                  placeholder="Enter technologist name"
                />
              </div>
              <div className="form-group">
                <label>Facility</label>
                <select
                  value={formData.facilityId}
                  onChange={(e) => setFormData({...formData, facilityId: e.target.value})}
                >
                  <option value="">No Facility (System-wide)</option>
                  {facilities.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <small>Leave blank for technologists available to all facilities</small>
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={formData.professionRole}
                  onChange={(e) => setFormData({...formData, professionRole: e.target.value})}
                >
                  <option value="sonographer">Sonographer</option>
                  <option value="nurse">Nurse</option>
                </select>
                <small>Controls which worksheets' Technologist dropdown this name appears in</small>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editing ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Technologists Table */}
      <div className="tech-table-container">
        {loading ? (
          <div className="loading">Loading technologists...</div>
        ) : technologists.length === 0 ? (
          <div className="no-data">
            <p>No technologists found.</p>
            <p>Click "Add Technologist" to create one.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Facility</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {technologists.map(tech => (
                <tr key={tech.id}>
                  <td className="tech-name">{tech.full_name}</td>
                  <td>{tech.facility_name || <em>System-wide</em>}</td>
                  <td>{tech.profession_role === 'nurse' ? 'Nurse' : 'Sonographer'}</td>
                  <td>
                    <span className={`status-badge ${tech.is_active ? 'active' : 'inactive'}`}>
                      {tech.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatServerDate(tech.created_at)}</td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleEdit(tech)} 
                      className="btn-sm btn-edit"
                      title="Edit"
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(tech)} 
                      className="btn-sm btn-danger"
                      title="Delete"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Help Text */}
      <div className="tech-help">
        <h4>ℹ️ About Technologist Management</h4>
        <ul>
          <li><strong>Facility Assignment:</strong> Technologists can be assigned to specific facilities or left as system-wide (available to all).</li>
          <li><strong>Alphabetical Sorting:</strong> Technologists appear in alphabetical order in form dropdowns.</li>
          <li><strong>Duplicate Names:</strong> Multiple technologists can have the same name (useful for common names).</li>
          <li><strong>Deletion:</strong> Technologists currently set as default for users cannot be deleted.</li>
        </ul>
      </div>
    </div>
  );
}

export default TechnologistManagement;
